import { Button } from '@optools/components/ui/button';
import { cn } from '@optools/components/ui/utils';
import { useCopyToClipboard } from '@optools/hooks/useCopyToClipboard';
import { Copy, CopyCheck } from 'lucide-react';
import { ReactNode } from 'react';

export type CopyTextProps = {
  className?: string;
} & (
  | {
      /** Primitive children (string, number, etc.) - textToCopy is optional */
      children: Extract<ReactNode, string | number | bigint | null | undefined>;
      textToCopy?: string;
    }
  | {
      /** Non-primitive children (JSX elements, etc.) - textToCopy is required */
      children: Exclude<ReactNode, string | number | bigint | null | undefined>;
      textToCopy: string;
    }
);

/**
 * Wraps children with a copy-to-clipboard button.
 *
 * @example
 * // With string children (textToCopy is optional)
 * <CopyText>192.168.1.1</CopyText>
 *
 * @example
 * // With JSX children (textToCopy is required)
 * <CopyText textToCopy="copy this"><span>Nested Content</span><Icon /></CopyText>
 */
export function CopyText({ children, className, textToCopy }: CopyTextProps) {
  const [copiedText, copy] = useCopyToClipboard();

  const handleCopy = () => {
    // If textToCopy is provided, use it; otherwise try to extract text from children
    const text = textToCopy ?? (typeof children === 'string' ? children : String(children));
    copy(text);
  };

  return (
    <div className={cn('group inline-flex items-center gap-1.5', className)}>
      {children}
      <Button
        className={cn(
          'h-auto p-1! opacity-0 transition-opacity group-hover:opacity-100',
          copiedText && 'opacity-100',
        )}
        variant="ghost"
        onClick={handleCopy}>
        {copiedText ? <CopyCheck className="size-4" /> : <Copy className="size-4" />}
      </Button>
    </div>
  );
}
